import { createSelector } from "reselect";
import { RootState } from "../reducers";
import { EditTposItemProps } from "../types/line-items";
import {
  ComboChoiceForOrder,
  OptionsType,
  OrderLinesWithOptions,
  SelectedLineOptions,
} from "../types/option";
import { lineItemsSelector } from "./lineItem";
import {
  basketFilter,
  findOptionInList,
  getOptionsForOrder,
  getSelectedOptions,
} from "../utils/options";
import _ from "lodash";
import { getAllProductsById } from "./product";

type State = RootState;

const getCurrentLineOptions = createSelector(
  [
    (state: State) => state.itemOptions.currentLineOptions,
    (_: State, props: EditTposItemProps) => Number(props.lineItemId),
  ],
  (currentLineOptions, lineItemId): OptionsType[] => {
    const options = currentLineOptions
      ? currentLineOptions[lineItemId]
      : undefined;
    return options || [];
  },
);

const getOptionsByLine = createSelector(
  [
    (state: State) => state.itemOptions.lineOptions,
    (_: State, props: Pick<EditTposItemProps, "lineItemId">) =>
      Number(props.lineItemId),
  ],
  (lineOptions, lineItemId): OptionsType[] => {
    return lineOptions[lineItemId] || [];
  },
);

const getAllOptions = (state: State) => {
  return state.itemOptions?.lineOptions;
};

const getAllCurrentLineOptions = (state: State) => {
  return state.itemOptions?.currentLineOptions;
};

const getSelectedChoicesForBasketLine = createSelector(
  [
    (state: State) => state.lineItems,
    (state: State) => getAllOptions(state),
    (_: State, props: Pick<EditTposItemProps, "lineItemId">) =>
      Number(props.lineItemId),
  ],
  (allLines, allOptions, lineId): SelectedLineOptions[] => {
    const line = allLines
      ? allLines.find((lineItem) => lineItem.id === lineId)
      : undefined;
    if (!line) {
      return [];
    }

    const { children } = line;
    const optionsList: SelectedLineOptions[] = [];
    if (children && children.length > 0) {
      children.forEach((optionLine) => {
        const options = allOptions?.[optionLine];
        const selectedOptions = options
          ? getSelectedOptions(options)
          : undefined;
        if (selectedOptions && selectedOptions.length > 0) {
          const areOptionsInList = findOptionInList(
            optionsList,
            selectedOptions,
          );
          if (!areOptionsInList) {
            optionsList.push({
              lineId: optionLine,
              quantity: 1,
              options: selectedOptions,
            });
          } else if (areOptionsInList.quantity) areOptionsInList.quantity += 1;
        }
      });
    } else {
      const options = allOptions?.[lineId];
      const selectedOptions = options ? getSelectedOptions(options) : undefined;
      if (selectedOptions && selectedOptions.length > 0)
        optionsList.push({
          lineId,
          options: selectedOptions,
        });
    }

    return optionsList;
  },
);

const getAllLineItems = createSelector(lineItemsSelector, (lines) => {
  if (!lines) {
    return [];
  }
  return lines.filter(basketFilter);
});

const getSelectedOptionsForOrder = (state: State): OrderLinesWithOptions[] => {
  const allLines = getAllLineItems(state);
  const linesWithOptions: OrderLinesWithOptions[] = [];

  allLines.forEach((line) => {
    const options = getSelectedChoicesForBasketLine(state, {
      lineItemId: `${line.id}`,
    });
    const numberOfChildren = line.children ? line.children.length : 0;
    let numberOfCustomisedLines = 0;

    if (line.children && numberOfChildren > 0) {
      options.forEach((option) => {
        const optionQuantity = option.quantity || 1;
        linesWithOptions.push({
          productId: line.productId,
          quantity: optionQuantity,
          choices: getOptionsForOrder(option.options),
        });
        numberOfCustomisedLines += optionQuantity;
      });
      if (numberOfChildren > numberOfCustomisedLines) {
        linesWithOptions.push({
          productId: line.productId,
          quantity: numberOfChildren - numberOfCustomisedLines,
        });
      }
    } else if (line.quantity) {
      const choices =
        options.length > 0 ? getOptionsForOrder(options[0].options) : undefined;
      const comboChoices = line.comboSelections
        ? Object.values(line.comboSelections)
            .flat()
            .map(
              (choice): ComboChoiceForOrder => ({
                productId: choice.productId,
                quantity: choice.quantity,
                isUpselling: choice.isUpselling ?? false,
              }),
            )
        : undefined;
      linesWithOptions.push({
        productId: line.productId,
        quantity: line.quantity,
        choices,
        comboChoices,
      });
    }
  });
  return linesWithOptions;
};

const getOptionPriceForBasketLine = (
  state: State,
  props: EditTposItemProps,
): number => {
  const lineId = props.lineItemId;

  const allLines = state.lineItems;
  const line = allLines
    ? allLines.find((lineItem) => lineItem.id === Number(lineId))
    : undefined;
  if (!line) {
    return 0;
  }

  const allOptions = getAllOptions(state);
  let totalPrice = 0;

  if (line.children && line.children.length > 0) {
    line.children.map((childId) => {
      const lineOptions = allOptions[`${childId}`];
      const selectedOptions = lineOptions
        ? getSelectedOptions(lineOptions)
        : undefined;

      if (selectedOptions) {
        const selectedOptionValues = selectedOptions[0];
        const selectedChoices = selectedOptionValues?.choices;
        const price = selectedChoices?.price;
        if (price) {
          totalPrice += price;
        }
      }
    });
  } else {
    const lineOptions = allOptions[`${line.id}`];
    const selectedOptions = lineOptions
      ? getSelectedOptions(lineOptions)
      : undefined;

    if (selectedOptions) {
      const selectedOptionValues = selectedOptions[0];
      const selectedChoices = selectedOptionValues?.choices;
      const price = selectedChoices?.price;
      if (price) {
        totalPrice = price * line.quantity;
      }
    }
  }
  return totalPrice;
};

const getOptionPriceForBasketLineTotal = (state: State): number => {
  const allOptions = getAllOptions(state);
  if (_.isEmpty(allOptions)) {
    return 0;
  }
  const allLines = state.lineItems;
  const linesDiscardingChildren = allLines.filter((line) => !line.parentId);

  let totalPrice = 0;
  linesDiscardingChildren.map((line) => {
    totalPrice += getOptionPriceForBasketLine(state, {
      lineItemId: `${line.id}`,
      onClose: () => {},
      onUpdatePress: () => {},
    });
  });
  return totalPrice;
};

type ComboChoiceDisplay = { productId: number; quantity: number; name: string };

const getComboChoicesForBasketLine = (
  state: State,
  lineItemId: number,
): ComboChoiceDisplay[] => {
  const lineItem = state.lineItems.find((item) => item.id === lineItemId);
  if (!lineItem?.comboSelections) return [];

  const products = getAllProductsById(state);
  return Object.values(lineItem.comboSelections)
    .flat()
    .filter((choice) => choice.quantity > 0)
    .map((choice) => ({
      productId: choice.productId,
      quantity: choice.quantity,
      name: products[choice.productId]?.name ?? "",
    }));
};

export type { ComboChoiceDisplay };
export {
  getCurrentLineOptions,
  getOptionsByLine,
  getAllLineItems,
  getSelectedChoicesForBasketLine,
  getAllOptions,
  getAllCurrentLineOptions,
  getSelectedOptionsForOrder,
  getOptionPriceForBasketLineTotal,
  getOptionPriceForBasketLine,
  getComboChoicesForBasketLine,
};
